import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

type NamedRole = { readonly code: string; readonly name: string };

/**
 * A role's name and description in plain words, in the console's language, keyed by the role's
 * code (FR-AD-5, decision 60). The code itself is never shown (FR-UX-3). A role the console has no
 * words for yet — one added to the catalog later — falls back to the catalog's own name, and has
 * no description rather than a made-up one.
 */
export function useRoleText(): {
  readonly name: (role: NamedRole) => string;
  readonly description: (role: NamedRole) => string | null;
} {
  const { t, i18n } = useTranslation('administration');
  const name = useCallback(
    (role: NamedRole) => {
      const key = `administration:roles.${role.code}.name`;
      return i18n.exists(key) ? t(key as never) : role.name;
    },
    [t, i18n],
  );
  const description = useCallback(
    (role: NamedRole) => {
      const key = `administration:roles.${role.code}.description`;
      return i18n.exists(key) ? t(key as never) : null;
    },
    [t, i18n],
  );
  return { name, description };
}
